<template>
    <div class="ai-floating-launcher" :class="{ idle: !recording }">
        <template v-if="recording">
            <button v-if="supported" type="button" class="ai-floating-open" :disabled="opening" @click="showPanel">
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="16" rx="2"/><rect x="11" y="11" width="8" height="7" rx="1"/></svg>
                {{ opening ? 'Opening controls…' : panel ? 'Show floating controls' : 'Float recording controls' }}
            </button>
            <span v-else class="ai-floating-help">Floating controls need desktop Chrome or Edge. Recording controls remain on this page.</span>
            <span v-if="error" class="ai-floating-help" role="status">{{ error }}</span>
        </template>
        <Teleport v-if="target" :to="target">
            <!-- The shared screen has no sound: ask before recording (AIScreenRecorder.start) -->
            <main v-if="guard && !guard.busy" class="floating-recorder guard" :class="{ 'with-guide': guard.attempts >= 2 }" role="alertdialog" aria-labelledby="guard-title" aria-describedby="guard-text">
                <h2 id="guard-title"><span class="status-dot starting"></span>No call audio</h2>
                <p id="guard-text">Only your microphone will be recorded. People on the call won’t be heard.</p>
                <p v-if="guard.attempts >= 2" class="share-note">{{ guideNote }}</p>
                <div class="guard-actions">
                    <button type="button" class="primary" @click="$emit('share-again')">Share again with sound</button>
                    <button type="button" @click="$emit('continue')">Continue — we’re all in the room</button>
                </div>
                <AiShareGuide v-if="guard.attempts >= 2" class="guide-after" />
            </main>
            <main v-else-if="!recording" class="floating-recorder" aria-label="Ironbook recording controls">
                <header>
                    <div class="recording-state"><span class="status-dot starting"></span><strong>Starting…</strong></div>
                    <span class="picker-note">Chrome’s picker is open</span>
                </header>
                <!-- While the picker is up this panel is the only Ironbook UI in view: a guide, not prose -->
                <AiShareGuide v-if="callAudio" :note="guard && guard.attempts >= 2 ? guideNote : ''" />
                <template v-else>
                    <p class="starting-help">Choose what to share in Chrome’s picker on the Ironbook page, then click <strong>Share</strong>. Recording starts right after.</p>
                </template>
                <footer>This panel closes if sharing is cancelled.</footer>
            </main>
            <main v-else class="floating-recorder" aria-label="Ironbook recording controls">
                <header>
                    <div class="recording-state"><span class="status-dot" :class="{ paused }"></span><strong>{{ paused ? 'Paused' : 'Recording' }}</strong></div>
                    <span class="timer" aria-label="Recorded duration">{{ timeLabel }}</span>
                </header>
                <div v-if="hasVideo" class="preview" :class="{ 'is-paused': paused }">
                    <video ref="preview" autoplay muted playsinline aria-label="Live preview of the shared screen"></video>
                    <span class="preview-caption">{{ previewUnavailable ? 'Preview unavailable · recording controls still work' : paused ? 'Paused · nothing is being recorded' : 'Live preview · ' + sourceLabel }}</span>
                </div>
                <div v-else class="audio-only">Audio recording · no screen video</div>
                <div class="audio-status">
                    <div><span class="audio-dot" :class="{ off: !hasMicAudio }"></span><span>{{ hasMicAudio ? 'Microphone on' : 'Microphone not included' }}</span>
                        <span v-if="hasMicAudio" class="mic-meter" role="meter" aria-label="Microphone input level" :aria-valuenow="paused ? 0 : Math.round(micLevel * 100)" aria-valuemin="0" aria-valuemax="100"><span :style="{ width: (paused ? 0 : Math.round(micLevel * 100)) + '%' }"></span></span>
                    </div>
                    <div><span class="audio-dot" :class="{ off: !hasSharedAudio }"></span><span>{{ hasSharedAudio ? 'Computer audio included' : 'Computer audio not shared' }}</span></div>
                </div>
                <p v-if="!hasSharedAudio" class="audio-hint">People on the call won’t be in the recording. Next time, choose <strong>Entire Screen</strong> and turn on <strong>Share with system audio</strong> in Chrome’s picker. A single window never shares sound.</p>
                <div class="actions">
                    <button type="button" @click="$emit('pause')">{{ paused ? '▶ Resume' : 'Ⅱ Pause' }}</button>
                    <button type="button" class="stop" @click="$emit('stop')">■ Stop recording</button>
                </div>
                <footer>Keep Ironbook open. Closing this panel keeps recording.</footer>
            </main>
        </Teleport>
    </div>
</template>

<script lang="ts">
import { defineComponent, markRaw } from 'vue';
import AIShareGuide from './AIShareGuide.vue';

// PiP has a separate document: keep its styles self-contained (no external assets,
// site cookies, cloned application DOM or dependency on the host page's theme).
const panelStyles = `
:root { color-scheme: dark; font-family: system-ui, -apple-system, "Segoe UI", sans-serif; color: #fafafa; background: #191a1e; }
* { box-sizing: border-box; }
body { margin: 0; }
.floating-recorder { padding: 14px; width: 100%; }
header, .recording-state, .audio-status > div, .actions { display: flex; align-items: center; }
header { justify-content: space-between; margin-bottom: 12px; }
.recording-state { gap: 8px; font-size: 14px; }
.status-dot { width: 9px; height: 9px; border-radius: 50%; background: #ff586c; }
.status-dot.paused { background: #f3ba55; }
.status-dot.starting { background: #9fa2ad; }
.starting-help { font-size: 13px; line-height: 1.45; margin: 0 0 10px; }
.picker-note { font-size: 11px; color: #bfc1ca; }
/* AIShareGuide (its scoped styles don't reach this document) */
.share-guide svg { display: block; width: 100%; height: auto; }
.share-steps { list-style: none; padding: 0; margin: 10px 0 0; display: grid; gap: 7px; font-size: 13px; }
.share-steps li { display: flex; align-items: center; gap: 8px; }
.share-steps b { flex: 0 0 20px; height: 20px; border-radius: 50%; background: #c2410c; color: #fff; font-size: 11px; display: inline-flex; align-items: center; justify-content: center; }
.share-note { margin: 8px 0 0; font-size: 12px; font-weight: 600; color: #e9c486; }
.guard h2 { display: flex; align-items: center; gap: 8px; font-size: 17px; margin: 2px 0 6px; }
.guard h2 .status-dot { background: #f3ba55; }
.guard p { font-size: 13px; line-height: 1.45; margin: 0 0 12px; color: #e6e7eb; }
.guard-actions { display: grid; gap: 8px; }
.guard-actions button { min-height: 40px; font-size: 13px; }
button.primary { background: #c2410c; border-color: #ea6a0e; }
button.primary:hover { background: #d4500f; }
.guide-after { margin-top: 12px; padding-top: 12px; border-top: 1px solid #42444d; }
/* Second share without sound: choice + note + guide must fit the 340×425 panel */
.with-guide { padding-top: 10px; padding-bottom: 8px; }
.with-guide h2 { margin-bottom: 4px; }
.with-guide p { margin-bottom: 6px; }
.with-guide .share-note { margin: 0 0 8px; }
.with-guide .guard-actions { grid-template-columns: 1fr 1fr; }
.with-guide .guard-actions button { font-size: 12px; line-height: 1.25; padding: 4px 6px; }
.with-guide .guide-after { margin-top: 8px; padding-top: 8px; }
.with-guide .share-guide svg { max-width: 236px; margin: 0 auto; }
.with-guide .share-steps { margin-top: 6px; gap: 3px; font-size: 12px; }
.timer { font-variant-numeric: tabular-nums; font-size: 18px; font-weight: 700; }
.preview { position: relative; background: #08090b; border: 1px solid #42444d; border-radius: 8px; overflow: hidden; aspect-ratio: 16 / 9; }
video { display: block; width: 100%; height: 100%; object-fit: contain; }
.is-paused video { visibility: hidden; }
.preview-caption { position: absolute; bottom: 0; left: 0; right: 0; padding: 5px 8px; background: #08090be8; font-size: 11px; color: #eee; }
.is-paused .preview-caption { top: 0; display: flex; align-items: center; justify-content: center; }
.audio-only { padding: 24px 10px; background: #25262c; text-align: center; border-radius: 8px; font-size: 13px; }
.audio-status { margin-top: 12px; font-size: 12px; }
.audio-status > div { gap: 7px; min-height: 24px; }
.audio-dot { flex: 0 0 6px; height: 6px; border-radius: 50%; background: #74d99c; }
.audio-dot.off { background: #f3ba55; }
.mic-meter { margin-left: auto; width: 48px; height: 5px; background: #444650; border-radius: 4px; overflow: hidden; }
.mic-meter > span { display: block; height: 100%; background: #74d99c; transition: width .2s; }
.audio-hint { color: #e9c486; font-size: 11px; line-height: 1.4; margin: 4px 0 0; }
.actions { gap: 8px; margin-top: 12px; }
button { min-height: 38px; border: 1px solid #62646e; border-radius: 7px; background: #30323a; color: #fff; font: inherit; font-size: 12px; font-weight: 650; cursor: pointer; flex: 1; }
button:hover { background: #444650; }
button:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
button.stop { background: #b92e3e; border-color: #d74758; }
button.stop:hover { background: #d3384a; }
footer { color: #bfc1ca; font-size: 10px; line-height: 1.4; margin-top: 10px; }
@media (max-width: 260px) { .floating-recorder { padding: 10px; } .actions { flex-direction: column; align-items: stretch; } }
`;

export default defineComponent({
    name: 'AIFloatingRecorder',
    components: { AiShareGuide: AIShareGuide },
    props: {
        // Meeting recordings: the Starting state is the share-with-sound guide
        callAudio: Boolean,
        // null, or { attempts, busy } while AIScreenRecorder asks about a share without sound
        guard: Object,
        // false while Start is still waiting for the screen-share picker
        recording: Boolean,
        displayStream: Object,
        paused: Boolean,
        timeLabel: { type: String, default: '0:00' },
        hasMicAudio: Boolean,
        hasSharedAudio: Boolean,
        micLevel: { type: Number, default: 0 }
    },
    emits: ['pause', 'stop', 'share-again', 'continue'],
    data() {
        return { panel: null as any, target: null as any, opening: false, error: '', previewUnavailable: false, disposed: false, request: 0 };
    },
    computed: {
        guideNote(): string { return 'Chrome shows the toggle under the screen preview.'; },
        supported(): boolean { return !!(window as any).documentPictureInPicture?.requestWindow; },
        hasVideo(): boolean { return !!(this.displayStream as any)?.getVideoTracks().some((t: any) => t.readyState === 'live'); },
        sourceLabel(): string {
            // The source itself is authoritative; never label the Playbook page
            // as the captured screen just because the controls live here.
            const surface = (this.displayStream as any)?.getVideoTracks()[0]?.getSettings().displaySurface;
            return surface === 'monitor' ? 'shared screen' : surface === 'window' ? 'shared window' : surface === 'browser' ? 'shared tab' : 'shared content';
        }
    },
    watch: {
        // The panel can open before capture exists (auto-open on Start), so the
        // preview is attached whenever the panel, recording and stream line up.
        recording() { this.attachStream(); },
        displayStream() { this.attachStream(); },
        hasVideo() { this.attachStream(); }
    },
    beforeUnmount() {
        this.disposed = true;
        this.closePanel();
    },
    methods: {
        showPanel() {
            if (this.panel && !this.panel.closed) { this.panel.focus(); return; }
            this.openNow();
        },
        // Step 1: open the window. MUST be called synchronously inside a click
        // handler (before any await, getDisplayMedia or upload): requestWindow
        // needs, and consumes, that click's user activation. Returns false when
        // nothing was requested (unsupported, already open, unmounted).
        openNow(): boolean {
            if (this.disposed || this.opening || !this.supported) return false;
            if (this.panel && !this.panel.closed) return false;
            this.opening = true;
            this.error = '';
            this.previewUnavailable = false;
            const request = ++this.request;
            let pending: Promise<any>;
            try {
                pending = (window as any).documentPictureInPicture.requestWindow({ width: 340, height: 425 });
            } catch (e) {
                pending = Promise.reject(e);
            }
            pending.then((panel: any) => {
                // Closed (picker cancelled) or unmounted while the window was opening
                if (this.disposed || request !== this.request) { panel.close(); return; }
                this.panel = markRaw(panel);
                panel.document.title = 'Ironbook · Recording';
                const style = panel.document.createElement('style');
                style.textContent = panelStyles;
                panel.document.head.appendChild(style);
                this.target = markRaw(panel.document.createElement('div'));
                panel.document.body.appendChild(this.target);
                panel.addEventListener('pagehide', () => {
                    if (this.panel === panel) this.releasePanel();
                }, { once: true });
                this.attachStream();
            }).catch(() => {
                if (this.disposed || request !== this.request) return;
                this.releasePanel();
                // Only worth saying once recording runs; before that the page's own controls show progress
                this.error = 'Floating controls could not open. Recording continues; use the controls here and try again.';
            }).finally(() => {
                if (request === this.request) this.opening = false;
            });
            return true;
        },
        // Step 2: show the live preview once capture has started. Safe to call
        // repeatedly; does nothing until the panel, recording and video exist.
        async attachStream() {
            await this.$nextTick();
            const panel = this.panel;
            if (this.disposed || !panel || panel.closed || !this.recording) return;
            const video = this.$refs.preview as HTMLVideoElement;
            if (!video) return;
            const tracks = (this.displayStream as any)?.getVideoTracks() || [];
            const current = (video.srcObject as MediaStream | null)?.getVideoTracks() || [];
            if (tracks.length && current.length === tracks.length && current.every((t, i) => t === tracks[i])) return;
            // Share video tracks only. The preview must NEVER play captured
            // audio back into speakers/mic, and must not own/stop these tracks.
            video.muted = true;
            video.srcObject = tracks.length ? new MediaStream(tracks) : null;
            if (tracks.length) await video.play().catch(() => { if (this.panel === panel) this.previewUnavailable = true; });
        },
        releasePanel() {
            const video = this.$refs.preview as HTMLVideoElement;
            if (video) video.srcObject = null;
            this.target = null;
            this.panel = null;
        },
        closePanel() {
            this.request++; // a window still opening closes itself when it arrives
            this.opening = false;
            const panel = this.panel;
            this.releasePanel();
            if (panel && !panel.closed) panel.close();
        }
    }
});
</script>

<style scoped>
.ai-floating-launcher { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
/* Mounted before recording so Start can open the window; takes no space until then */
.ai-floating-launcher.idle { display: none; }
.ai-floating-open { display: inline-flex; align-items: center; gap: 6px; padding: 7px 11px; border: 1px solid #d5bca7; border-radius: 7px; background: #fff8f1; color: #73380d; font: inherit; font-size: 12px; font-weight: 650; cursor: pointer; }
.ai-floating-open:hover { background: #ffecd9; }
.ai-floating-open:focus-visible { outline: 2px solid #ad4c0c; outline-offset: 2px; }
.ai-floating-open:disabled { cursor: wait; opacity: .7; }
.ai-floating-help { font-size: 12px; max-width: 420px; color: #73523d; }
</style>
